import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import ParabolaShape from "../../../../components/visuals/ParabolaShape";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


const TYPE = "MaturaDerivativeOptimizationOpenTask";

function rand(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x || 1;
}

function fracLatex(num, den) {
  if (den < 0) return fracLatex(-num, -den);
  const g = gcd(num, den);
  const n = num / g;
  const d = den / g;
  return d === 1 ? String(n) : String.raw`\frac{${n}}{${d}}`;
}

function coeffSqrt3Latex(num, den = 1) {
  if (den < 0) return coeffSqrt3Latex(-num, -den);
  const g = gcd(num, den);
  const n = num / g;
  const d = den / g;
  if (d === 1) return String.raw`${n}\sqrt3`;
  return String.raw`\frac{${n}\sqrt3}{${d}}`;
}

function rawValue(value) {
  if (value == null) return "";
  if (typeof value === "object" && value.target && "value" in value.target) {
    return String(value.target.value ?? "");
  }
  if (typeof value === "object") {
    for (const key of ["raw", "text", "value", "latex", "expr"]) {
      if (value[key] != null && typeof value[key] !== "object") {
        return String(value[key]);
      }
    }
  }
  return String(value);
}

function normalize(raw) {
  return rawValue(raw)
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\cdot|\\times|·|×/g, "*")
    .replace(/\\sqrt\s*\{?(\d+)\}?/g, "sqrt$1")
    .replace(/√\s*(\d+)/g, "sqrt$1")
    .replace(/\\pi|π/g, "pi")
    .replace(/\\frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, "($1)/($2)")
    .replace(/\{|\}/g, "")
    .replace(/,/g, ".")
    .replace(/\s+/g, "");
}

function hasAll(source, tokens) {
  const s = normalize(source);
  return tokens.every((token) => s.includes(normalize(token)));
}

function hasAny(source, tokenGroups) {
  return tokenGroups.some((tokens) => hasAll(source, tokens));
}

function setField(answer, key, value, onChange) {
  onChange && onChange({ ...(answer || {}), [key]: value });
}

function latexFromRaw(raw) {
  const s = rawValue(raw).trim();
  if (!s) return "?";
  return s
    .replace(/sqrt\(([^)]+)\)/gi, "\\sqrt{$1}")
    .replace(/sqrt(\d+)/gi, "\\sqrt{$1}")
    .replace(/pi/gi, "\\pi")
    .replace(/\*/g, "\\cdot ")
    .replace(/\^\(([^)]+)\)/g, "^{$1}")
    .replace(/\^([a-z0-9]+)/gi, "^{$1}");
}

function makeConeVariant(c) {
  const c2 = c * c;
  const c3 = c * c * c;
  const minLatex = coeffSqrt3Latex(3 * c3, 2);
  return {
    id: "cone-rational",
    symbol: "V",
    variable: "h",
    extremumType: "najmniejsze",
    functionLatex: String.raw`V(h)=\frac{${c2}h^3}{h^2-${c2}}`,
    domainLatex: String.raw`h\in(${c},+\infty)`,
    taskText:
      "Objetosc V bryly, jako funkcja wysokosci h, wyraza sie wzorem",
    askText:
      "Wyznacz wysokosc tej z rozwazanych bryl, ktorej objetosc jest najmniejsza. Oblicz te najmniejsza objetosc. Zapisz obliczenia.",
    derivativeLatex: String.raw`V'(h)=${c2}\cdot\frac{3h^2(h^2-${c2})-2h^4}{(h^2-${c2})^2}`,
    derivativeSimplifiedLatex: String.raw`V'(h)=\frac{${c2}h^2(h^2-${3 * c2})}{(h^2-${c2})^2}`,
    criticalLatex: String.raw`h=${c}\sqrt3`,
    signLatex: String.raw`V'(h)<0\text{ dla }h\in(${c},${c}\sqrt3),\quad V'(h)>0\text{ dla }h\in(${c}\sqrt3,+\infty)`,
    resultLatex: String.raw`h=${c}\sqrt3,\quad V_{\min}=${minLatex}`,
    derivativeChecks: [
      [`${c2}`, "3h^2", `h^2-${c2}`, "2h^4", `(h^2-${c2})^2`],
      [`${c2}`, "h^2", `h^2-${3 * c2}`, `(h^2-${c2})^2`],
    ],
    criticalChecks: [[`${c}sqrt3`]],
    resultChecks: [
      [`${3 * c3}`, "sqrt3", "/2"],
      [`${(3 * c3) / 2}`, "sqrt3"],
    ],
    meta: { c, c2, c3, minLatex },
  };
}

function makePrismVariant(a0) {
  // V(a)=A*a-(1/8)a^3, dobrane tak, aby punkt krytyczny byl calkowity.
  const linear = (3 * a0 * a0) / 8;
  const linearLatex = fracLatex(3 * a0 * a0, 8);
  const maxValue = linear * a0 - (a0 ** 3) / 8;
  const maxLatex = fracLatex(a0 ** 3, 4);

  return {
    id: "prism-polynomial",
    symbol: "V",
    variable: "a",
    extremumType: "najwieksze",
    functionLatex: String.raw`V(a)=${linearLatex}a-\frac{1}{8}a^3`,
    domainLatex: String.raw`a\in(0,${a0}\sqrt3)`,
    taskText:
      "Objetosc V graniastoslupa, jako funkcja dlugosci a krawedzi podstawy, wyraza sie wzorem",
    askText:
      "Wyznacz dlugosc krawedzi podstawy tego z rozwazanych graniastoslupow, ktorego objetosc jest najwieksza. Oblicz te najwieksza objetosc. Zapisz obliczenia.",
    derivativeLatex: String.raw`V'(a)=${linearLatex}-\frac{3}{8}a^2`,
    derivativeSimplifiedLatex: String.raw`V'(a)=${linearLatex}-\frac{3}{8}a^2`,
    criticalLatex: String.raw`a=${a0}`,
    signLatex: String.raw`V'(a)>0\text{ dla }a\in(0,${a0}),\quad V'(a)<0\text{ dla }a\in(${a0},${a0}\sqrt3)`,
    resultLatex: String.raw`a=${a0},\quad V_{\max}=${maxLatex}`,
    derivativeChecks: [[String(linear), "3/8", "a^2"], [`${3 * a0 * a0}/8`, "3/8", "a^2"]],
    criticalChecks: [[String(a0)]],
    resultChecks: [[String(maxValue)]],
    meta: { a0, linear, linearLatex, maxValue, maxLatex },
  };
}

export function generateTask(options = {}) {
  const isOriginal = options.variant === "original" && options.session === "2025_CZE";
  const variant =
    !isOriginal && Math.random() < 0.5
      ? makeConeVariant(rand([3, 4, 5, 6, 7, 8]))
      : makePrismVariant(isOriginal ? 4 : rand([4, 6, 8, 10, 12]));
  return {
    type: TYPE,
    points: 4,
    variantId: variant.id,
    data: variant.meta,
    source: isOriginal
      ? { variant: "original", exam: "MATURA", session: "2025_CZE", taskNumber: "12.2", basedOn: "MMAP-R0-100-2506-12.2" }
      : variant.id === "prism-polynomial"
        ? { variant: "random", basedOn: "MMAP-R0-100-2506-12.2" }
        : { variant: "random" },
  };
}

function getVariant(task) {
  if (task.variantId === "cone-rational") {
    return makeConeVariant(task.data?.c || 5);
  }
  return makePrismVariant(task.data?.a0 || 4);
}

export function renderTask(task) {
  const v = getVariant(task);
  return (
    <div className="space-y-4">
      <p>
        <strong>Zadanie maturalne - otwarte (0-4 pkt).</strong>
      </p>
      <p>{v.taskText}</p>
      <BlockMath math={v.functionLatex} />
      <p>
        dla <InlineMath math={v.domainLatex} />.
      </p>
      {v.extraInfo || null}
      <p>
        <strong>{v.askText}</strong>
      </p>
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  const v = getVariant(task);
  const yLabel = v.symbol;
  const derivativeLabel = `${v.symbol}'(${v.variable})`;
  const resultLabel = v.id === "cone-rational" ? "V_{\\min}" : "V_{\\max}";
  return (
    <div className="space-y-5">
      <OpenTaskScoringHint variant="independent" />
      <div>
        <p>
          <strong>Krok 1 (1 pkt).</strong> Oblicz pochodna funkcji{" "}
          <InlineMath math={yLabel} />.
        </p>
        <div style={{ marginBottom: 6 }}>
          <InlineMath math={`${derivativeLabel} =`} />
        </div>
        <EquationInput
          value={userAnswer.derivative || ""}
          onChange={(value) => setField(userAnswer, "derivative", value, onChange)}
          disabled={disabled}
          placeholder={
            v.id === "cone-rational"
              ? `np. ${v.meta.c2}${v.variable}^2(${v.variable}^2-${3 * v.meta.c2})/(${v.variable}^2-${v.meta.c2})^2`
              : `np. ${v.meta.linearLatex.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/, "$1/$2")}-3/8${v.variable}^2`
          }
          showPreview={true}
          label={derivativeLabel}
          basis="520px"
        />
      </div>

      <div>
        <p>
          <strong>Krok 2 (1 pkt).</strong> Wyznacz punkt krytyczny nalezacy do dziedziny.
        </p>
        <div style={{ marginBottom: 6 }}>
          <InlineMath math={`${v.variable} =`} />
        </div>
        <EquationInput
          value={userAnswer.critical || ""}
          onChange={(value) => setField(userAnswer, "critical", value, onChange)}
          disabled={disabled}
          placeholder={v.id === "cone-rational" ? `np. ${v.meta.c}sqrt3` : `np. ${v.meta.a0}`}
          showPreview={true}
          label={v.variable}
          basis="320px"
        />
      </div>

      <div>
        <p>
          <strong>Krok 3 (1 pkt).</strong> Uzasadnij, ze w tym punkcie funkcja
          przyjmuje wartosc {v.extremumType}.
        </p>
        <EquationInput
          value={userAnswer.justification || ""}
          onChange={(value) => setField(userAnswer, "justification", value, onChange)}
          disabled={disabled}
          placeholder="np. znak pochodnej: + potem -"
          showPreview={true}
          basis="520px"
        />
      </div>

      <div>
        <p>
          <strong>Krok 4 (1 pkt).</strong> Podaj wynik koncowy.
        </p>
        <div style={{ marginBottom: 6 }}>
          <InlineMath math={`${resultLabel} =`} />
        </div>
        <EquationInput
          value={userAnswer.result || ""}
          onChange={(value) => setField(userAnswer, "result", value, onChange)}
          disabled={disabled}
          placeholder={v.id === "cone-rational" ? `np. ${v.meta.minLatex.replace(/\\frac\{([^{}]+)\\sqrt3\}\{([^{}]+)\}/, "$1sqrt3/$2").replace(/\\sqrt3/g, "sqrt3")}` : `np. ${v.meta.maxValue}`}
          showPreview={true}
          label={resultLabel}
          basis="520px"
        />
      </div>
    </div>
  );
}

function checkJustification(raw, variant) {
  const s = normalize(raw);
  if (!s) return false;
  const hasWords =
    /rosn|malej|pochodn|znak|monotonic|maks|max|min|najw|najm/.test(s);
  const hasSigns = s.includes("+") && s.includes("-");
  const hasCritical = hasAny(s, variant.criticalChecks);
  return hasWords || (hasSigns && hasCritical) || (hasSigns && /0/.test(s));
}

export function checkAnswer(task, userAnswer = {}) {
  const v = getVariant(task);
  const derivativeOk = hasAny(userAnswer.derivative, v.derivativeChecks);
  const criticalOk = hasAny(userAnswer.critical, v.criticalChecks);
  const justificationOk = checkJustification(userAnswer.justification, v);
  const resultOk = hasAny(userAnswer.result, v.resultChecks);

  const parts = [
    { key: "derivative", label: "pochodna funkcji", correct: derivativeOk },
    { key: "critical", label: "punkt krytyczny w dziedzinie", correct: criticalOk },
    { key: "justification", label: "uzasadnienie ekstremum", correct: justificationOk },
    { key: "result", label: "wynik koncowy", correct: resultOk },
  ];
  const score = parts.filter((part) => part.correct).length;

  return {
    isCorrect: score === 4,
    correct: score === 4,
    score,
    maxScore: 4,
    parts,
  };
}

export function renderUserAnswer(userAnswer = {}) {
  if (!userAnswer || Object.keys(userAnswer).length === 0) {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div className="space-y-1">
      {userAnswer.derivative ? (
        <p>
          Pochodna: <InlineMath math={latexFromRaw(userAnswer.derivative)} />
        </p>
      ) : null}
      {userAnswer.critical ? (
        <p>
          Punkt krytyczny: <InlineMath math={latexFromRaw(userAnswer.critical)} />
        </p>
      ) : null}
      {userAnswer.justification ? (
        <p>
          Uzasadnienie: <InlineMath math={latexFromRaw(userAnswer.justification)} />
        </p>
      ) : null}
      {userAnswer.result ? (
        <p>
          Wynik: <InlineMath math={latexFromRaw(userAnswer.result)} />
        </p>
      ) : null}
    </div>
  );
}

export function renderAnswer(task) {
  const v = getVariant(task);
  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> <InlineMath math={v.derivativeSimplifiedLatex} />
      </p>
      <p>
        <strong>Krok 2:</strong> <InlineMath math={v.criticalLatex} />
      </p>
      <p>
        <strong>Krok 3:</strong> <InlineMath math={v.signLatex} />
      </p>
      <p>
        <strong>Krok 4:</strong> <InlineMath math={v.resultLatex} />
      </p>
    </div>
  );
}

function renderConeExplanation(v) {
  const { c, c2, c3 } = v.meta;
  return (
    <>
      <p>
        <strong>Krok 1.</strong> Liczymy pochodna funkcji wymiernej.
      </p>
      <BlockMath math={v.derivativeLatex} />
      <BlockMath math={v.derivativeSimplifiedLatex} />

      <p>
        <strong>Krok 2.</strong> Szukamy miejsc zerowych pochodnej w dziedzinie.
      </p>
      <BlockMath math={String.raw`h^2(h^2-${3 * c2})=0`} />
      <BlockMath math={String.raw`h=${c}\sqrt3`} />
      <BlockMath math={String.raw`${c}\sqrt3\in(${c},+\infty)`} />

      <p>
        <strong>Krok 3.</strong> Sprawdzamy znak pochodnej.
      </p>
      <BlockMath math={v.signLatex} />
      <div style={{ maxWidth: 560, margin: "18px auto" }}>
        <ParabolaShape
          aSign={1}
          rootMode="two"
          rootLeft={-2}
          rootRight={2}
          labelLeft={String.raw`-${c}\sqrt3`}
          labelRight={String.raw`${c}\sqrt3`}
          xRange={5}
          yWindow={5}
          showXAxis
          showZeroTicks
          curveColor="#22c55e"
          width={560}
          height={200}
        />
      </div>
      <p>
        W dziedzinie <InlineMath math={String.raw`(${c},+\infty)`} /> istotny jest
        tylko dodatni pierwiastek pochodnej. Na lewo od{" "}
        <InlineMath math={v.criticalLatex} /> pochodna jest ujemna, a na prawo
        dodatnia, czyli funkcja najpierw maleje, a potem rosnie.
      </p>
      <p>
        Pochodna zmienia znak z <InlineMath math="-" /> na{" "}
        <InlineMath math="+" />, wiec objetosc jest najmniejsza dla{" "}
        <InlineMath math={v.criticalLatex} />.
      </p>

      <p>
        <strong>Krok 4.</strong> Obliczamy najmniejsza objetosc.
      </p>
      <BlockMath math={String.raw`V(${c}\sqrt3)=\frac{${c2}(${c}\sqrt3)^3}{(${c}\sqrt3)^2-${c2}}=${v.meta.minLatex}`} />
      <BlockMath math={v.resultLatex} />
    </>
  );
}

function renderPrismExplanation(v) {
  const { a0, linearLatex, maxLatex } = v.meta;
  return (
    <>
      <p>
        <strong>Krok 1.</strong> Liczymy pochodna funkcji wielomianowej.
      </p>
      <BlockMath math={v.derivativeLatex} />

      <p>
        <strong>Krok 2.</strong> Wyznaczamy punkt krytyczny z dziedziny.
      </p>
      <BlockMath math={String.raw`${linearLatex}-\frac{3}{8}a^2=0`} />
      <BlockMath math={String.raw`a^2=${a0 * a0},\quad a=${a0}`} />
      <BlockMath math={String.raw`${a0}\in(0,${a0}\sqrt3)`} />

      <p>
        <strong>Krok 3.</strong> Sprawdzamy znak pochodnej.
      </p>
      <BlockMath math={v.signLatex} />
      <div style={{ maxWidth: 560, margin: "18px auto" }}>
        <ParabolaShape
          aSign={-1}
          rootMode="two"
          rootLeft={-2}
          rootRight={2}
          labelLeft={`-${v.meta.a0}`}
          labelRight={String(v.meta.a0)}
          xRange={5}
          yWindow={5}
          showXAxis
          showZeroTicks
          curveColor="#f59e0b"
          width={560}
          height={200}
        />
      </div>
      <p>
        W dziedzinie <InlineMath math={String.raw`(0,${v.meta.a0}\sqrt3)`} /> istotny jest
        tylko dodatni pierwiastek pochodnej. Na lewo od{" "}
        <InlineMath math={v.criticalLatex} /> pochodna jest dodatnia, a na prawo
        ujemna, czyli funkcja najpierw rosnie, a potem maleje.
      </p>
      <p>
        Pochodna zmienia znak z <InlineMath math="+" /> na{" "}
        <InlineMath math="-" />, wiec objetosc jest najwieksza dla{" "}
        <InlineMath math={v.criticalLatex} />.
      </p>

      <p>
        <strong>Krok 4.</strong> Obliczamy najwieksza objetosc.
      </p>
      <BlockMath math={String.raw`V(${a0})=${linearLatex}\cdot${a0}-\frac{1}{8}\cdot${a0}^3=${maxLatex}`} />
      <BlockMath math={v.resultLatex} />
    </>
  );
}

export function renderExplanation(task) {
  const v = getVariant(task);
  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      {v.id === "cone-rational" ? renderConeExplanation(v) : null}
      {v.id === "prism-polynomial" ? renderPrismExplanation(v) : null}
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
